import * as io from "socket.io-client";
import store from "../redux/store";
import {
  fetchNotifications,
  setAllNotifications,
} from "../redux/features/notificationSlice";

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.isInitialized = false;
  }

  // Connect to socket server
  connect(userId) {
    // If already connected, don't reconnect
    if (this.isConnected()) {
      console.log("Socket already connected, reusing existing connection");

      // If userId is provided and different from before, update notification listener
      if (userId && this.currentUserId !== userId) {
        this.setupNotificationListener(userId);
        this.currentUserId = userId;
      }

      return this.socket;
    }

    try {
      const token = localStorage.getItem("token");
      console.log(token);
      if (!token) {
        console.error("No token found for socket connection");
        return null;
      }

      console.log("API URL:", process.env.REACT_APP_API_BASE_URL);
      console.log("Establishing new socket connection");

      // Use REACT_APP_API_BASE_URL if provided, otherwise default to localhost:4000
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

      // Create a new socket connection (connect to /auth namespace)
      this.socket = io(`${baseUrl}/auth`, {
        transports: ["websocket", "polling"],
        query: { token },
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 5000,
      });

      // Store the current user ID
      this.currentUserId = userId;

      // Add event listeners for connection status
      this.socket.on("connect", () => {
        console.log("Socket connected:", this.socket.id);
        this.isInitialized = true;
        if (this.socket !== undefined) {
          this.socket.emit("notifications");
        }
      });

      this.socket.on("notifications", (notifications) => {
        console.log("+++++++++++++");
        // Wrap dispatch in microtask to avoid "Cannot update a component while rendering" 
        Promise.resolve().then(() => {
            store.dispatch(fetchNotifications());
        });
        console.log("------------------");
        console.log(notifications);
      });

      this.socket.on("disconnect", (reason) => {
        console.log("Socket disconnected:", reason);
        // Wrap dispatch in microtask
        Promise.resolve().then(() => {
            store.dispatch(fetchNotifications());
        });
        // Only set to not initialized if it's a permanent disconnection
        if (
          reason === "io server disconnect" ||
          reason === "io client disconnect"
        ) {
          this.isInitialized = false;
        }
      });

      this.socket.on("connect_error", (err) => {
        console.log("Socket connection error:", err);
      });

      // Set up notification listener for this user
      if (userId) {
        this.setupNotificationListener(userId);
      }

      return this.socket;
    } catch (error) {
      console.error("Error setting up socket connection:", error);
      return null;
    }
  }

  // Set up notification listener for a specific user
  setupNotificationListener(userId, callback) {
    if (!this.socket) {
      console.error("Socket not connected");
      return;
    }

    const eventName = `notification:application:${userId}`;

    // Remove existing listener if any
    if (this.listeners.has(eventName)) {
      this.socket.off(eventName, this.listeners.get(eventName));
    }

    // Create new listener
    const listener = (newNotification) => {
      console.log(newNotification);
      if (callback) {
        callback(newNotification);
      }
    };

    // Store listener reference for cleanup
    this.listeners.set(eventName, listener);

    // Add listener to socket
    this.socket.on(eventName, listener);
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      console.log("Disconnecting socket");

      // Remove all listeners
      this.listeners.forEach((listener, event) => {
        this.socket.off(event, listener);
      });
      this.listeners.clear();

      // Disconnect socket
      this.socket.disconnect();
      this.socket = null;
      this.isInitialized = false;
      this.currentUserId = null;
    }
  }

  // Get socket instance
  getSocket() {
    return this.socket;
  }

  // Check if socket is connected
  isConnected() {
    return this.socket && this.socket.connected && this.isInitialized;
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
